import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchPeopleRoles,
  fetchPeopleRole,
  createPeopleRole,
  updatePeopleRole,
} from "@/api/peopleRoles";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Save } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { PeopleRole } from "@/types";

const PeopleRoleForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const isNewRole = !id || id === "new";
  const title = isNewRole ? "Add Role" : "Edit Role";
  const roleId = isNewRole ? null : parseInt(id!, 10);

  // Fetch all roles (for validation of duplicate names)
  const { data: roles = [] } = useQuery({
    queryKey: ["peopleRoles"],
    queryFn: fetchPeopleRoles,
  });

  // Default role state
  const defaultRole = {
    id: 0,
    name: "",
    description: "",
    isActive: true,
  };

  // Form state
  const [role, setRole] = useState<PeopleRole>(defaultRole);
  const [errors, setErrors] = useState({
    name: "",
  });
  const [isSaving, setIsSaving] = useState(false);

  // If editing, fetch current role data
  useEffect(() => {
    const loadRole = async () => {
      if (!isNewRole && roleId) {
        try {
          const roleData = await fetchPeopleRole(roleId);
          if (roleData) {
            setRole(roleData);
          } else {
            // Role not found, redirect to roles list
            toast({
              title: "Role not found",
              description: "The requested role does not exist.",
              variant: "destructive",
            });
            navigate("/admin/system/people-roles");
          }
        } catch (error) {
          console.error("Error loading role:", error);
          toast({
            title: "Error loading role",
            description: "There was a problem loading the role details.",
            variant: "destructive",
          });
          navigate("/admin/system/people-roles");
        }
      }
    };

    loadRole();
  }, [roleId, isNewRole, navigate, toast]);

  // Handle input changes
  const handleInputChange = (field: string, value: string | boolean) => {
    setRole((prev) => ({ ...prev, [field]: value }));

    // Clear errors when user types
    if (field === "name" && errors.name) {
      setErrors((prev) => ({ ...prev, name: "" }));
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors = { name: "" };
    let isValid = true;

    if (!role.name.trim()) {
      newErrors.name = "Role name is required";
      isValid = false;
    } else if (
      roles.some(
        (r) =>
          r.name.toLowerCase() === role.name.toLowerCase() && r.id !== role.id
      )
    ) {
      newErrors.name = "A role with this name already exists";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);

    try {
      if (isNewRole) {
        // Create new role
        await createPeopleRole(role.name, role.description);
        toast({
          title: "Role created",
          description: `${role.name} has been created successfully.`,
        });
      } else if (roleId) {
        // Update existing role
        await updatePeopleRole(roleId, {
          name: role.name,
          description: role.description,
          isActive: role.isActive,
        });
        toast({
          title: "Role updated",
          description: `${role.name} has been updated successfully.`,
        });
      }

      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ["peopleRoles"] });

      // Redirect back to people roles management
      navigate("/admin/system/people-roles");
    } catch (error) {
      console.error("Error saving role:", error);

      // Show error message
      let errorMessage = "An error occurred while saving the role";
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AdminLayout title={title}>
      <div className="mb-6">
        <Button variant="ghost" className="gap-2" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
          Back to People Roles
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          <CardDescription>
            {isNewRole
              ? "Create a new role for people such as authors, translators, or narrators."
              : "Update the details of an existing role."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">
                Role Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                value={role.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                className={errors.name ? "border-destructive" : ""}
                placeholder="e.g. Author, Translator, Narrator"
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={role.description || ""}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                placeholder="Role description (optional)"
                rows={3}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="isActive"
                checked={role.isActive}
                onCheckedChange={(checked) =>
                  handleInputChange("isActive", checked)
                }
              />
              <Label htmlFor="isActive">Active</Label>
            </div>

            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving} className="gap-2">
                {isSaving && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                )}
                <Save className="h-4 w-4" />
                Save Role
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

export default PeopleRoleForm;
